import React, { useEffect, useRef, useState } from "react";
import { Box, makeStyles, Fade } from "@material-ui/core";
import { useTranslate } from "react-polyglot";
import { useHistory } from "react-router-dom";
import { useCourseProvider } from "../../../Providers/Data/CourseProvider.jsx";
import DownloadLink from "../../../SharedComponents/DownloadLink.jsx";
import { extractFileNameFromUrl, LEARN_PAGE_TOP_ANCHOR_ID } from "../../../Utils/misc.js";
import PrimaryRoundedButton from "../../../SharedComponents/PrimaryRoundedButton.jsx";
import PdfTheory, { makePdfTitle } from "./PdfTheory.jsx";

/**
 * Возвращает список pdf-файлов теории с запасным вариантом для старых данных,
 * где был только одиночный файл theory.pdf.
 *
 * @param {Object} theory - теория урока.
 * @returns {Array} список pdf-файлов теории.
 */
export const getTheoryPdfs = (theory) => {
  if (!theory) {
    return [];
  }

  if (theory.pdfs?.length > 0) {
    return theory.pdfs;
  }

  return theory.pdf ? [theory.pdf] : [];
};

/**
 * Возвращает элементы списка теории для дерева структуры урока: html-текст (всегда
 * первым, если он есть) и pdf-файлы. Если элементов больше одного, в дереве структуры
 * отображается аккордеон теории.
 *
 * @param {Object} theory - теория урока.
 * @param {string} theoryLabel - название элемента html-текста.
 * @returns {Array<{anchorId: string, title: string}>} элементы списка теории.
 */
export const makeTheoryTreeItems = (theory, theoryLabel) => [
  ...(theory?.html ? [{ anchorId: LEARN_PAGE_TOP_ANCHOR_ID, title: theoryLabel }] : []),
  ...getTheoryPdfs(theory).map((pdf, index) => ({
    anchorId: `theory-pdf-${index}`,
    title: makePdfTitle(pdf.name),
  })),
];

const useStyles = makeStyles((theme) => ({
  theoryRoot: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    margin: "0 0 30px",
  },
  infoText: {
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexGrow: 1,
    [theme.breakpoints.up("lg")]: {
      flexGrow: "unset",
      height: 600,
    },
  },
  iFrame: {
    width: "100%",
    border: "none",
    flexGrow: 1,
    minHeight: 700,
  },
  downloadLink: {
    padding: 10,
    backgroundColor: "#f5f5f5",
    borderRadius: 5,
  },
}));

/**
 * @returns {*}
 * @constructor
 */
const Theory = () => {
  const classes = useStyles();
  const t = useTranslate();
  const { currentDeck, markDeckLearned, currentBranch } = useCourseProvider();
  const history = useHistory();
  /**
   * Локальное состояние и ссылки для работы с iframe (теория в формате HTML)
   */
  const iFrameRef = useRef(null);
  // Кнопка по-умолчанию скрыта, если есть html-контент; иначе (PDF/файл) — показана
  const [showProceedButton, setShowProceedButton] = useState(!currentDeck?.theory?.html);
  // Храним ссылки на обработчики событий, чтобы корректно удалять их при размонтировании/смене контента
  const iframeListenersRef = useRef({ scroll: null, resize: null });
  // Есть ли в теории хотя бы один pdf-файл (с запасным вариантом для старых данных с одиночным theory.pdf)
  const hasAnyPdf = getTheoryPdfs(currentDeck?.theory).length > 0;

  /**
   * Сбрасываем видимость кнопки при смене html-контента урока.
   * Если html отсутствует — кнопку показываем сразу (логика ограничений касается только iframe-контента).
   */
  useEffect(() => {
    // Если есть PDF в теории — кнопку показываем всегда, независимо от HTML и скролла
    if (hasAnyPdf) {
      setShowProceedButton(true);
    } else if (currentDeck?.theory?.html) {
      // Если есть только HTML — по умолчанию скрываем кнопку до выполнения условий
      setShowProceedButton(false);
    } else {
      // Если нет ни HTML, ни PDF — кнопку показываем (например, только доп. файл)
      setShowProceedButton(true);
    }
    // При смене контента — также выполним очистку слушателей
    return () => {
      try {
        const win = iFrameRef.current?.contentWindow;
        if (win && iframeListenersRef.current.scroll) {
          win.removeEventListener("scroll", iframeListenersRef.current.scroll, { passive: true });
        }
        if (win && iframeListenersRef.current.resize) {
          win.removeEventListener("resize", iframeListenersRef.current.resize);
        }
      } catch (e) {
        // Ничего: может быть недоступен contentWindow, если iframe уже разрушен
      }
      iframeListenersRef.current = { scroll: null, resize: null };
    };
  }, [currentDeck?.theory?.html, hasAnyPdf]);

  /**
   * Обработчик загрузки iframe:
   * 1) Ждём, когда контент будет доступен.
   * 2) Если вертикального скролла нет — показываем кнопку сразу.
   * 3) Если скролл есть — показываем кнопку, когда пользователь проскроллит до 95%.
   */
  const onIframeLoad = () => {
    // Если есть PDF — слушатели на iframe не нужны, кнопку показываем сразу
    if (getTheoryPdfs(currentDeck?.theory).length > 0) {
      setShowProceedButton(true);
      return;
    }
    // Если html-контента нет — ограничения не нужны
    if (!currentDeck?.theory?.html) return;

    const iframe = iFrameRef.current;
    if (!iframe) return;

    // Пытаемся получить документ и окно iframe. Работает, т.к. используем srcDoc (same-origin).
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    const win = iframe.contentWindow;
    if (!doc || !win) return;

    // Удаляем старые слушатели (на всякий случай)
    if (iframeListenersRef.current.scroll) {
      try {
        win.removeEventListener("scroll", iframeListenersRef.current.scroll, { passive: true });
      } catch {
        // Ничего: может быть недоступен contentWindow, если iframe уже разрушен
      }
    }
    if (iframeListenersRef.current.resize) {
      try {
        win.removeEventListener("resize", iframeListenersRef.current.resize);
      } catch {
        // Ничего: может быть недоступен contentWindow, если iframe уже разрушен
      }
    }
    iframeListenersRef.current = { scroll: null, resize: null };

    /** Берём основной скроллируемый элемент */
    const getScrollEl = () => doc.scrollingElement || doc.documentElement || doc.body;

    /** Проверка наличия скролла и прогресса */
    const hasVerticalScroll = () => {
      const el = getScrollEl();
      if (!el) return false;
      return el.scrollHeight - el.clientHeight > 2; // небольшая погрешность
    };

    /** Проверка прогресса и переключение кнопки */
    const checkProgressAndToggle = () => {
      const el = getScrollEl();
      if (!el) return;
      // Используем hasVerticalScroll() для определения, есть ли вертикальный скролл
      if (!hasVerticalScroll()) {
        // Скроллить некуда — показываем кнопку
        setShowProceedButton(true);
        return;
      }
      const maxScroll = el.scrollHeight - el.clientHeight;
      const progress = el.scrollTop / maxScroll;
      if (progress >= 0.95) {
        // Достигли порога — показываем кнопку и больше не скрываем при обратном скролле
        setShowProceedButton(true);
      }
    };

    /** Навешиваем слушатели скролла и ресайза для динамического контента */
    const onScroll = () => {
      checkProgressAndToggle();
    };

    /** Обработчик изменения размера окна */
    const onResize = () => {
      checkProgressAndToggle();
    };

    iframeListenersRef.current.scroll = onScroll;
    iframeListenersRef.current.resize = onResize;

    try {
      win.addEventListener("scroll", onScroll, { passive: true });
      win.addEventListener("resize", onResize);
    } catch (e) {
      // В редких случаях доступ может быть ограничен — тогда просто не ставим слушатели
    }

    // Первая инициализация: сразу проверим состояние
    checkProgressAndToggle();
    // На случай поздней подгрузки изображений — повторные проверки с задержкой
    setTimeout(checkProgressAndToggle, 300);
    setTimeout(checkProgressAndToggle, 1200);
  };

  if (!currentDeck) {
    return <Box className={classes.infoText}>{t("loading")}</Box>;
  }

  const additionalFileUrl = currentDeck.theory.additionalFileUrl;
  const theoryPdfs = getTheoryPdfs(currentDeck.theory);
  const hasPdf = theoryPdfs.length > 0;

  if (!currentDeck.theory.html && !hasPdf && !additionalFileUrl) {
    return <Box className={classes.infoText}>{t("noTheory")}</Box>;
  }

  const additionalFileName = additionalFileUrl ? extractFileNameFromUrl(additionalFileUrl) : null;

  /** */
  const onProceedButtonClick = () => {
    // Определяем наличие «материалов» в смысле слова/упражнения (теория не считается материалом для этого правила)
    const hasVideos = (currentDeck?.videos?.items || []).length > 0;
    const hasWords = (currentDeck?.words?.items || []).length > 0;
    const hasExercises = (currentDeck?.exercises?.items || []).length > 0;
    const hasAnyMaterials = hasWords || hasExercises;

    // Если урок ещё не выучен и в нём нет никаких материалов – отмечаем его как выученный.
    // Последующие переходы из этого урока реализованы внутри markDeckLearned (по ответу сервера).
    // Видео идёт отдельно, чтобы переключение между табами происходило без учёта видео.
    if (currentDeck.learned === false && !hasAnyMaterials && !hasVideos) {
      markDeckLearned();
      return;
    }

    // Если урок уже выучен и в нём нет материалов – идём к следующему доступному и невыученному уроку в текущем разделе.
    if (currentDeck.learned === true && !hasAnyMaterials) {
      const decks = currentBranch?.decks || [];
      const currentIndex = decks.findIndex((d) => d.id === currentDeck.id);
      let nextOpenNotLearned = null;
      for (let i = currentIndex + 1; i < decks.length; i++) {
        const d = decks[i];
        if (d.open && d.learned === false) {
          nextOpenNotLearned = d;
          break;
        }
      }
      if (nextOpenNotLearned) {
        history.push(`/learn/${nextOpenNotLearned.id}`);
      } else {
        history.push(`/`);
      }
      return;
    }

    // В остальных случаях переключаемся на следующую релевантную вкладку.
    // Приоритет: "Слова" -> "Упражнения".
    if (hasWords) {
      window.dispatchEvent(new CustomEvent("Learning.SwitchTab", { detail: { target: "words" } }));
    } else if (hasExercises) {
      window.dispatchEvent(new CustomEvent("Learning.SwitchTab", { detail: { target: "exercises" } }));
    }
  };

  return (
    <Box className={classes.theoryRoot}>
      {additionalFileUrl && (
        <Box margin="20px">
          <DownloadLink
            className={classes.downloadLink}
            linkText={t("theoryAdditionalFile", { filename: additionalFileName })}
            fileName={additionalFileName}
            href={additionalFileUrl}
          />
        </Box>
      )}
      {currentDeck.theory.html && (
        // ВАЖНО: используем srcDoc, чтобы иметь доступ к содержимому iframe (same-origin)
        <iframe ref={iFrameRef} className={classes.iFrame} srcDoc={currentDeck.theory.html} onLoad={onIframeLoad} />
      )}
      {theoryPdfs.map((pdf, index) => (
        <PdfTheory key={pdf.url || index} id={`theory-pdf-${index}`} pdf={pdf} />
      ))}
      <Fade
        in={Boolean(hasPdf || !currentDeck.theory.html || showProceedButton)}
        timeout={{ enter: 400 }}
        mountOnEnter
        unmountOnExit
      >
        <Box display="flex" justifyContent="center" margin="30px 0 40px">
          <PrimaryRoundedButton onClick={onProceedButtonClick}>{t("Decks.proceed")}</PrimaryRoundedButton>
        </Box>
      </Fade>
    </Box>
  );
};

export default Theory;
